import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Grid2X2, Bookmark, Link as LinkIcon } from "lucide-react";

const WorkflowSection = () => {
  return (
    <section className="py-20 px-6">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12">
        <div className="md:w-1/2">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Never lose inspiration again
          </h2>
          <p className="text-lg text-clustr-darkGray mb-8">
            Clustr helps you capture, organize, and access your digital inspiration seamlessly.
          </p>
          
          <ul className="space-y-4">
            <li className="flex items-start">
              <div className="bg-blue-100 p-2 rounded-full mr-4 mt-1">
                <LinkIcon className="h-5 w-5 text-clustr-blue" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Save anything from the web</h3>
                <p className="text-clustr-darkGray">
                  Websites, articles, images, videos, and more - capture them all with a single click.
                </p>
              </div>
            </li>
            
            <li className="flex items-start">
              <div className="bg-pink-100 p-2 rounded-full mr-4 mt-1">
                <Grid2X2 className="h-5 w-5 text-clustr-magenta" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Organize your way</h3>
                <p className="text-clustr-darkGray">
                  Group content into Collections, cross-reference with Tags, or arrange visually on Boards.
                </p>
              </div>
            </li>
            
            <li className="flex items-start">
              <div className="bg-purple-100 p-2 rounded-full mr-4 mt-1">
                <Bookmark className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Access your library anywhere</h3>
                <p className="text-clustr-darkGray">
                  Your complete collection is available whenever and wherever inspiration strikes.
                </p>
              </div>
            </li>
          </ul>
          
          <div className="mt-10">
            <Button asChild className="bg-clustr-blue hover:bg-blue-600 transition-colors">
              <Link to="/auth">
                Start building your library <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
        
        <div className="md:w-1/2 relative">
          <div className="bg-gradient-to-br from-clustr-blue/10 to-clustr-magenta/10 rounded-2xl p-1">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d" 
                alt="Clustr interface" 
                className="w-full h-auto"
              />
            </div>
          </div>
          
          <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg border border-clustr-lightGray animate-float">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 rounded-full bg-clustr-blue"></div>
              <div className="text-sm font-medium">Design Inspirations</div>
            </div>
          </div>
          
          <div className="absolute -top-6 -left-6 bg-white p-4 rounded-lg shadow-lg border border-clustr-lightGray animate-float" style={{ animationDelay: "1s" }}>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 rounded-full bg-clustr-magenta"></div>
              <div className="text-sm font-medium">UX Research</div>
            </div>
          </div>

          <div className="absolute top-1/4 -right-10 bg-white p-4 rounded-lg shadow-lg border border-clustr-lightGray animate-float" style={{ animationDelay: "1.5s" }}>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="text-sm font-medium">Articles & Citations</div>
            </div>
          </div>

          <div className="absolute bottom-1/4 -left-10 bg-white p-4 rounded-lg shadow-lg border border-clustr-lightGray animate-float" style={{ animationDelay: "2s" }}>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <div className="text-sm font-medium">Code Snippets</div>
            </div>
          </div>

          <div className="absolute top-10 left-1/3 bg-white p-4 rounded-lg shadow-lg border border-clustr-lightGray animate-float" style={{ animationDelay: "2.5s" }}>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <div className="text-sm font-medium">Mood Imagery</div>
            </div>
          </div>

          <div className="absolute bottom-10 right-1/3 bg-white p-4 rounded-lg shadow-lg border border-clustr-lightGray animate-float" style={{ animationDelay: "3s" }}>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="text-sm font-medium">Video Clips</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowSection;
